import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { statusBadgeClasses, statusLabel, formatDate } from "@/lib/statusColor";
import { toast } from "sonner";
import { ArrowLeft, Search, MessageSquare, ChevronDown, ChevronRight, Download, Trash2 } from "lucide-react";
import { deleteTaskWithUndo } from "@/lib/deleteWithUndo";

function statusRowTint(color) {
  switch (color) {
    case "green": return "bg-emerald-50/70 border-l-4 border-l-emerald-500";
    case "yellow": return "bg-amber-50/70 border-l-4 border-l-amber-500";
    case "red": return "bg-rose-50/70 border-l-4 border-l-rose-500";
    case "completed": return "bg-slate-100/70 border-l-4 border-l-slate-400";
    default: return "bg-white border-l-4 border-l-slate-200";
  }
}

export default function Report() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expanded, setExpanded] = useState({});
  const [commentsMap, setCommentsMap] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tasks");
        setTasks(data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const assignees = useMemo(() => {
    const set = new Map();
    for (const t of tasks) set.set(t.assignee_email, t.assignee_name);
    return Array.from(set.entries()).map(([email, name]) => ({ email, name }));
  }, [tasks]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tasks
      .filter((t) => assigneeFilter === "all" || t.assignee_email === assigneeFilter)
      .filter((t) => {
        if (statusFilter === "all") return true;
        if (statusFilter === "completed") return t.status === "completed";
        return t.color === statusFilter && t.status !== "completed";
      })
      .filter((t) => !q || (
        (t.title || "").toLowerCase().includes(q) ||
        (t.description || "").toLowerCase().includes(q) ||
        (t.assignee_name || "").toLowerCase().includes(q)
      ))
      .sort((a, b) => new Date(b.assigned_at) - new Date(a.assigned_at));
  }, [tasks, query, assigneeFilter, statusFilter]);

  const toggleExpand = async (task) => {
    const isOpen = !!expanded[task.id];
    setExpanded((prev) => ({ ...prev, [task.id]: !isOpen }));
    if (!isOpen && !commentsMap[task.id]) {
      try {
        const { data } = await api.get(`/tasks/${task.id}/comments`);
        setCommentsMap((prev) => ({ ...prev, [task.id]: data }));
      } catch (err) {
        toast.error(formatApiError(err));
      }
    }
  };

  const deleteTask = (task) => {
    deleteTaskWithUndo({
      task,
      onHide: (t) => setTasks((prev) => prev.filter((x) => x.id !== t.id)),
      onRestore: (t) => setTasks((prev) => [...prev, t]),
    });
  };

  const exportCSV = () => {
    const rows = [["Date Assigned", "Assignee", "Email", "Title", "Description", "Deadline", "Status", "Priority", "Tags", "Created By", "Latest Comment", "Total Comments"]];
    for (const t of filtered) {
      rows.push([
        new Date(t.assigned_at).toLocaleString(),
        t.assignee_name, t.assignee_email,
        t.title, (t.description || "").replace(/\n/g, " "),
        new Date(t.deadline).toLocaleString(),
        statusLabel(t.color, t.status),
        t.priority || "",
        (t.tags || []).join(", "),
        t.created_by_display || "",
        t.latest_comment ? `${t.latest_comment.author}: ${t.latest_comment.text}` : "",
        String(t.comment_count || 0),
      ]);
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `taskflow-report-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Assignment report
            </h1>
            <p className="text-slate-500 mt-2">Date-wise view of every task with status and comment history.</p>
          </div>
          <Button variant="outline" onClick={exportCSV} data-testid="export-csv-btn">
            <Download size={14} className="mr-1.5" /> Export CSV
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className="relative md:col-span-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search tasks…" className="pl-9 bg-white h-10" data-testid="report-search" />
          </div>
          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="h-10 bg-white" data-testid="report-assignee-select"><SelectValue placeholder="All assignees" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All assignees</SelectItem>
              {assignees.map((a) => <SelectItem key={a.email} value={a.email}>{a.name} ({a.email})</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-10 bg-white" data-testid="report-status-select"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="green">On Track</SelectItem>
              <SelectItem value="yellow">Past Deadline</SelectItem>
              <SelectItem value="red">Overdue</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card className="rounded-md border-slate-200 shadow-sm overflow-hidden" data-testid="report-table-card">
          {loading ? (
            <div className="p-8 text-slate-500 text-sm text-center">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-slate-500 text-sm text-center">No tasks match the filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="report-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-3 font-semibold w-8"></th>
                    <th className="px-3 py-3 font-semibold">Date Assigned</th>
                    <th className="px-3 py-3 font-semibold">Assignee</th>
                    <th className="px-3 py-3 font-semibold">Task</th>
                    <th className="px-3 py-3 font-semibold">Deadline</th>
                    <th className="px-3 py-3 font-semibold">Status</th>
                    <th className="px-3 py-3 font-semibold">Created By</th>
                    <th className="px-3 py-3 font-semibold">Recent comment</th>
                    <th className="px-3 py-3 font-semibold w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => {
                    const isOpen = !!expanded[t.id];
                    const list = commentsMap[t.id];
                    return (
                      <>
                        <tr key={t.id} className={`border-b border-slate-100 hover:bg-slate-50/60 transition-colors ${statusRowTint(t.color)}`} data-testid={`report-row-${t.id}`}>
                          <td className="px-3 py-3 align-top">
                            <button type="button" onClick={() => toggleExpand(t)} className="text-slate-500 hover:text-slate-900 p-1" data-testid={`report-expand-${t.id}`} aria-label="Expand comments">
                              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                          </td>
                          <td className="px-3 py-3 align-top text-xs text-slate-600 whitespace-nowrap">{formatDate(t.assigned_at)}</td>
                          <td className="px-3 py-3 align-top">
                            <div className="text-slate-900 font-medium">{t.assignee_name}</div>
                            <div className="text-[11px] text-slate-500">{t.assignee_email}</div>
                          </td>
                          <td className="px-3 py-3 align-top">
                            <Link to={`/tasks/${t.id}`} className="text-slate-900 font-semibold hover:underline">{t.title}</Link>
                            {t.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2 max-w-md">{t.description}</p>}
                            {t.parent_task_id && <Badge variant="outline" className="mt-1 text-[10px] border-slate-300">Sub-task</Badge>}
                          </td>
                          <td className="px-3 py-3 align-top text-xs text-slate-600 whitespace-nowrap">{formatDate(t.deadline)}</td>
                          <td className="px-3 py-3 align-top">
                            <Badge variant="outline" className={`border ${statusBadgeClasses(t.color)}`}>
                              {statusLabel(t.color, t.status)}
                            </Badge>
                          </td>
                          <td className="px-3 py-3 align-top" data-testid={`report-created-by-${t.id}`}>
                            {t.created_by_display ? (
                              <div>
                                <div className="text-xs text-slate-800 font-medium">{t.created_by_display}</div>
                                {t.created_by_role === "assignee" && (
                                  <Badge variant="outline" className="mt-0.5 text-[9px] px-1 py-0 h-4 border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50">Assignee</Badge>
                                )}
                                {t.created_by_role === "deleted" && (
                                  <Badge variant="outline" className="mt-0.5 text-[9px] px-1 py-0 h-4 border-slate-300 text-slate-500 bg-slate-100">Deleted</Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 align-top max-w-xs">
                            {t.latest_comment ? (
                              <div>
                                <div className="text-[11px] text-slate-500">
                                  <span className="font-medium text-slate-700">{t.latest_comment.author}</span> · {formatDate(t.latest_comment.created_at)}
                                </div>
                                <p className="text-xs text-slate-700 line-clamp-2 mt-0.5">{t.latest_comment.text}</p>
                                {t.comment_count > 1 && (
                                  <button type="button" onClick={() => toggleExpand(t)} className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-0.5 mt-1">
                                    <MessageSquare size={10} /> {t.comment_count} total · {isOpen ? "hide" : "see all"}
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">No comments</span>
                            )}
                          </td>
                          <td className="px-3 py-3 align-top">
                            <button
                              type="button"
                              onClick={() => deleteTask(t)}
                              className="text-rose-500 hover:text-rose-700 p-1"
                              title="Delete task"
                              data-testid={`report-delete-${t.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-slate-50/60 border-b border-slate-100" data-testid={`report-comments-${t.id}`}>
                            <td colSpan={9} className="px-3 py-3">
                              <div className="pl-6">
                                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                                  All comments ({list ? list.length : "…"})
                                </div>
                                {!list ? (
                                  <p className="text-xs text-slate-500">Loading…</p>
                                ) : list.length === 0 ? (
                                  <p className="text-xs text-slate-500">No comments yet.</p>
                                ) : (
                                  <ul className="space-y-2">
                                    {list.map((c) => (
                                      <li key={c.id} className="border-l-2 border-slate-300 pl-3">
                                        <div className="text-[11px]">
                                          <span className="font-medium text-slate-900">{c.author}</span>
                                          <span className="text-slate-400 ml-2">{formatDate(c.created_at)}</span>
                                        </div>
                                        <p className="text-xs text-slate-700 mt-0.5">{c.text}</p>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        <p className="text-[11px] text-slate-500 mt-3" data-testid="report-summary">Showing {filtered.length} of {tasks.length} task{tasks.length === 1 ? "" : "s"}.</p>
      </main>
    </div>
  );
}
